/** @file moveHandles: the move tool's meshes (shafts, cone heads, plane squares, centre square), built once. */
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder.js';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { axesLocal } from './axesLocal';
import type { GizmoFrame, HandleSet } from './gizmoFrame';
import { gizmoLine, type GizmoLine } from './gizmoLine';
import { GIZMO_METRICS as M } from './gizmoMetrics';
import type { GizmoNodes } from './gizmoNodes';
import { GIZMO_PALETTE } from './gizmoPalette';
import type { GizmoHandle } from './gizmoShape';
import { gizmoUnit } from './gizmoUnit';
import { handlePaint } from './handlePaint';
import { lineWidth } from './lineWidth';
import { planeSigns } from './planeSigns';
import { thinSolids, type ThinSolids } from './thinSolids';
import { unitSquare } from './unitSquare';
import { VIEWPORT_GROUPS } from './viewportGroups';

const AXES = [0, 1, 2] as const;

/** Unit-frame axis `k` scaled by `s`, in Babylon local coordinates. */
const axis = (k: number, s = 1): Vec3 => axesLocal([k === 0 ? s : 0, k === 1 ? s : 0, k === 2 ? s : 0]);

/** Writes instance `i`'s matrix rows: local x, y, z to the Babylon vectors given (scaled), then the position. */
function setMatrix(m: Float32Array, i: number, rows: readonly Vec3[]): void {
  rows.forEach((r, k) => { m.set([r[0], r[1], r[2], k === 3 ? 1 : 0], 16 * i + 4 * k); });
}

/** Cone `k`: its height (Babylon local y) along axis `k`, from the shaft end (radius 1) to the tip. */
function coneMatrices(cones: ThinSolids): void {
  const mid = 1 + gizmoUnit(M.coneLength) / 2;
  for (const k of AXES) setMatrix(cones.matrices, k, [axis((k + 1) % 3), axis(k), axis((k + 2) % 3), axis(k, mid)]);
  cones.markMatrices();
}

/** Plane `n`: the unit square (Babylon local xy) over the two other axes, centred `planeAt` on the viewer's sides. */
function planeMatrices(planes: ThinSolids, signs: readonly number[]): void {
  const size = gizmoUnit(M.planeSize);
  for (const n of AXES) {
    const u = (n + 1) % 3;
    const v = (n + 2) % 3;
    const a = axis(u, (signs[u] ?? 1) * M.planeAt);
    const b = axis(v, (signs[v] ?? 1) * M.planeAt);
    setMatrix(planes.matrices, n, [axis(u, size), axis(v, size), axis(n), [a[0] + b[0], a[1] + b[1], a[2] + b[2]]]);
  }
  planes.markMatrices();
}

/** Three shafts from `shaftFrom` to the radius (GreasedLine), one per axis. */
function shaftLines(scene: Scene, nodes: GizmoNodes): GizmoLine[] {
  return AXES.map((k) => gizmoLine(`viewport-gizmo-move-${String(k)}`, [axis(k, M.shaftFrom), axis(k)], nodes.frame, scene, GIZMO_PALETTE.handle[k] ?? GIZMO_PALETTE.hover));
}

/**
 * Shafts (GreasedLine) and cone heads (`CreateCylinder`, three thin instances) on the axes frame, plane squares
 * (thin instances, flipped toward the viewer each frame) and the centre square outline on the screen node.
 */
export function moveHandles(scene: Scene, nodes: GizmoNodes): HandleSet {
  const shafts = shaftLines(scene, nodes);
  const c = gizmoUnit(M.centreSquare) / 2;
  const centre = gizmoLine('viewport-gizmo-move-centre', [...unitSquare(0, 1, [-c, c], [-c, c]), [-c, -c, 0]], nodes.screen, scene, GIZMO_PALETTE.hover);
  const cone = CreateCylinder('viewport-gizmo-cones', { height: gizmoUnit(M.coneLength), diameterTop: 0, diameterBottom: gizmoUnit(M.coneWidth), tessellation: 24 }, scene);
  const cones = thinSolids(cone, nodes.frame, 3, 1, VIEWPORT_GROUPS.gizmo);
  const planes = thinSolids(CreatePlane('viewport-gizmo-planes', { size: 1 }, scene), nodes.frame, 3, M.planeAlpha, VIEWPORT_GROUPS.gizmo);
  coneMatrices(cones);
  const signs: [number, number, number] = [1, 1, 1];
  let shown = NaN;
  const lines = [...shafts, centre];
  const axisMeshes = [...shafts.map((l) => l.mesh), cones.mesh, planes.mesh];
  const meshes = [...axisMeshes, centre.mesh];
  return {
    lines,
    setEnabled: (on) => { for (const m of meshes) m.setEnabled(on); },
    update: (f: GizmoFrame) => {
      for (const m of axisMeshes) m.setEnabled((f.show & 1) !== 0);
      centre.mesh.setEnabled((f.show & 8) !== 0);
      planeSigns(f.model.axes, f.view, signs);
      const key = signs[0] + 2 * signs[1] + 4 * signs[2];
      if (key !== shown) { planeMatrices(planes, signs); shown = key; }
      for (const k of AXES) {
        shafts[k]?.style(handlePaint(f, k), lineWidth(f, k));
        cones.paint(k, handlePaint(f, k));
        planes.paint(k, handlePaint(f, (4 + k) as GizmoHandle));
      }
      centre.style(handlePaint(f, 3), lineWidth(f, 3));
      cones.commit();
      planes.commit();
    },
  };
}
